import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Lock, LogOut, RefreshCw, Download } from "lucide-react";
import { SectionLabel } from "@/components/Reveal";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ax = axios.create({ baseURL: API, withCredentials: true });

const STATUS_OPTIONS = [
  { value: "new", label: "New", cls: "text-cyan-600 border-cyan-500/50" },
  { value: "contacted", label: "Contacted", cls: "text-amber-600 border-amber-600/50" },
  { value: "demo_done", label: "Demo Done", cls: "text-green-600 border-green-600/50" },
  { value: "closed", label: "Closed", cls: "text-zinc-500 border-zinc-900/25" },
];

const formatDetail = (d) => {
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return "Something went wrong.";
};

export default function Admin() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [creds, setCreds] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [leads, setLeads] = useState([]);
  const [newsletter, setNewsletter] = useState([]);
  const [roiLeads, setRoiLeads] = useState([]);
  const [msLeads, setMsLeads] = useState([]);
  const [engagements, setEngagements] = useState([]);
  const [engForm, setEngForm] = useState({ client: "", service: "", specialist: "", monthly_revenue: "", monthly_cost: "" });
  const [tab, setTab] = useState("demo");

  const loadData = useCallback(async () => {
    const [l, n, r, m, e] = await Promise.all([ax.get("/admin/leads"), ax.get("/admin/newsletter"), ax.get("/admin/roi"), ax.get("/admin/assessments"), ax.get("/admin/engagements")]);
    setLeads(l.data);
    setNewsletter(n.data);
    setRoiLeads(r.data);
    setMsLeads(m.data);
    setEngagements(e.data);
  }, []);

  useEffect(() => {
    ax.get("/auth/me")
      .then((r) => { setUser(r.data); loadData(); })
      .catch(() => {})
      .finally(() => setChecking(false));
  }, [loadData]);

  const login = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const { data } = await ax.post("/auth/login", creds);
      setUser(data);
      loadData();
      toast.success("Authenticated");
    } catch (err) {
      setError(formatDetail(err.response?.data?.detail) || err.message);
    }
  };

  const updateStatus = async (id, status) => {
    try {
      await ax.patch(`/admin/leads/${id}/status`, { status });
      setLeads((prev) => prev.map((l) => ((l._id || l.id) === id ? { ...l, status } : l)));
      toast.success("Status updated");
    } catch {
      toast.error("Status update failed.");
    }
  };

  const updateMsStatus = async (id, status) => {
    try {
      await ax.patch(`/admin/assessments/${id}/status`, { status });
      setMsLeads((prev) => prev.map((l) => ((l._id || l.id) === id ? { ...l, status } : l)));
      toast.success("Status updated");
    } catch {
      toast.error("Status update failed.");
    }
  };

  const addEngagement = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...engForm, monthly_revenue: Number(engForm.monthly_revenue) || 0, monthly_cost: Number(engForm.monthly_cost) || 0, status: "active" };
      const { data } = await ax.post("/admin/engagements", payload);
      setEngagements([data, ...engagements]);
      setEngForm({ client: "", service: "", specialist: "", monthly_revenue: "", monthly_cost: "" });
      toast.success("Engagement added");
    } catch {
      toast.error("Could not add engagement.");
    }
  };

  const updateEngStatus = async (eng, status) => {
    const id = eng._id || eng.id;
    try {
      await ax.patch(`/admin/engagements/${id}`, { client: eng.client, service: eng.service, specialist: eng.specialist, monthly_revenue: eng.monthly_revenue, monthly_cost: eng.monthly_cost, status });
      setEngagements((prev) => prev.map((x) => ((x._id || x.id) === id ? { ...x, status } : x)));
    } catch {
      toast.error("Update failed.");
    }
  };

  const deleteEngagement = async (id) => {
    try {
      await ax.delete(`/admin/engagements/${id}`);
      setEngagements((prev) => prev.filter((x) => (x._id || x.id) !== id));
    } catch {
      toast.error("Delete failed.");
    }
  };

  const exportCsv = async () => {
    const map = { demo: ["leads", "demo_leads.csv"], newsletter: ["newsletter", "newsletter_subscribers.csv"], ms: ["assessments", "managed_services_leads.csv"] };
    if (!map[tab]) { toast.error("No CSV export for this tab."); return; }
    const [which, filename] = map[tab];
    try {
      const res = await ax.get(`/admin/export/${which}.csv`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Export failed.");
    }
  };

  const logout = async () => {
    await ax.post("/auth/logout");
    setUser(null);
    setLeads([]);
    setNewsletter([]);
  };

  if (checking) return <main className="flex min-h-screen items-center justify-center pt-24"><p className="font-mono text-xs text-zinc-500">checking session…</p></main>;

  if (!user) {
    return (
      <main data-testid="admin-login-page" className="grid-bg flex min-h-screen items-center justify-center px-6 pt-24">
        <form onSubmit={login} className="w-full max-w-md border border-zinc-900/10 bg-[#FFFFFF] p-10">
          <Lock size={22} className="text-cyan-600" />
          <h1 className="mt-6 font-heading text-2xl font-semibold text-zinc-900">Admin Access</h1>
          <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400">restricted // lead retrieval</p>
          <div className="mt-8 space-y-4">
            <input data-testid="admin-email-input" type="email" required placeholder="Email" value={creds.email} onChange={(e) => setCreds({ ...creds, email: e.target.value })}
              className="w-full border border-zinc-900/20 bg-transparent px-4 py-3.5 text-sm text-zinc-900 placeholder:text-zinc-400 focus:border-cyan-500 focus:outline-none" />
            <input data-testid="admin-password-input" type="password" required placeholder="Password" value={creds.password} onChange={(e) => setCreds({ ...creds, password: e.target.value })}
              className="w-full border border-zinc-900/20 bg-transparent px-4 py-3.5 text-sm text-zinc-900 placeholder:text-zinc-400 focus:border-cyan-500 focus:outline-none" />
          </div>
          {error && <p data-testid="admin-login-error" className="mt-4 text-sm text-red-600">{error}</p>}
          <button data-testid="admin-login-btn" type="submit" className="btn-primary mt-8 w-full">Sign In</button>
        </form>
      </main>
    );
  }

  return (
    <main data-testid="admin-dashboard" className="mx-auto min-h-screen max-w-7xl px-6 pt-36 pb-24">
      <div className="flex flex-wrap items-center justify-between gap-6">
        <div>
          <SectionLabel>Lead Console</SectionLabel>
          <h1 className="mt-4 font-heading text-4xl font-semibold text-zinc-900">Inbound Pipeline</h1>
        </div>
        <div className="flex gap-3">
          <button data-testid="admin-export-btn" onClick={exportCsv} className="btn-secondary !px-4 !py-2.5">CSV <Download size={14} /></button>
          <button data-testid="admin-refresh-btn" onClick={loadData} className="btn-secondary !px-4 !py-2.5"><RefreshCw size={14} /></button>
          <button data-testid="admin-logout-btn" onClick={logout} className="btn-secondary !px-4 !py-2.5">Logout <LogOut size={14} /></button>
        </div>
      </div>

      <div className="mt-12 flex gap-2">
        <button data-testid="admin-tab-demo" onClick={() => setTab("demo")} className={`border px-5 py-2.5 font-mono text-xs uppercase tracking-[0.2em] transition-colors ${tab === "demo" ? "border-cyan-500 bg-cyan-600 text-white" : "border-zinc-900/20 text-zinc-600"}`}>
          Demo Requests ({leads.length})
        </button>
        <button data-testid="admin-tab-newsletter" onClick={() => setTab("newsletter")} className={`border px-5 py-2.5 font-mono text-xs uppercase tracking-[0.2em] transition-colors ${tab === "newsletter" ? "border-cyan-500 bg-cyan-600 text-white" : "border-zinc-900/20 text-zinc-600"}`}>
          Newsletter ({newsletter.length})
        </button>
        <button data-testid="admin-tab-roi" onClick={() => setTab("roi")} className={`border px-5 py-2.5 font-mono text-xs uppercase tracking-[0.2em] transition-colors ${tab === "roi" ? "border-cyan-500 bg-cyan-600 text-white" : "border-zinc-900/20 text-zinc-600"}`}>
          ROI Leads ({roiLeads.length})
        </button>
        <button data-testid="admin-tab-ms" onClick={() => setTab("ms")} className={`border px-5 py-2.5 font-mono text-xs uppercase tracking-[0.2em] transition-colors ${tab === "ms" ? "border-cyan-500 bg-cyan-600 text-white" : "border-zinc-900/20 text-zinc-600"}`}>
          Managed Services ({msLeads.length})
        </button>
        <button data-testid="admin-tab-eng" onClick={() => setTab("eng")} className={`border px-5 py-2.5 font-mono text-xs uppercase tracking-[0.2em] transition-colors ${tab === "eng" ? "border-cyan-500 bg-cyan-600 text-white" : "border-zinc-900/20 text-zinc-600"}`}>
          Engagements ({engagements.length})
        </button>
      </div>

      <div className="mt-8 overflow-x-auto border border-zinc-900/10">
        {tab === "demo" ? (
          <table data-testid="admin-leads-table" className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-zinc-900/10 bg-[#FFFFFF]">
                {["Status", "Name", "Email", "Company", "Role", "Industry", "Interest", "Slot", "Message", "Date"].map((h) => (
                  <th key={h} className="whitespace-nowrap px-4 py-3 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {leads.length === 0 && <tr><td colSpan={10} className="px-4 py-10 text-center text-zinc-400">No demo requests yet.</td></tr>}
              {leads.map((l) => {
                const id = l._id || l.id;
                const st = STATUS_OPTIONS.find((s) => s.value === (l.status || "new")) || STATUS_OPTIONS[0];
                return (
                <tr key={id} className="border-b border-zinc-900/5 transition-colors hover:bg-zinc-900/[0.03]">
                  <td className="whitespace-nowrap px-4 py-2">
                    <Select value={l.status || "new"} onValueChange={(v) => updateStatus(id, v)}>
                      <SelectTrigger data-testid={`lead-status-select-${id}`} className={`h-8 w-[130px] rounded-none border bg-transparent font-mono text-[10px] uppercase tracking-[0.15em] ${st.cls}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="rounded-none border-zinc-900/20 bg-[#FFFFFF] text-zinc-900">
                        {STATUS_OPTIONS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-zinc-900">{l.name}</td>
                  <td className="whitespace-nowrap px-4 py-3 text-cyan-600">{l.email}</td>
                  <td className="whitespace-nowrap px-4 py-3 text-zinc-700">{l.company}</td>
                  <td className="whitespace-nowrap px-4 py-3 text-zinc-500">{l.role || "—"}</td>
                  <td className="whitespace-nowrap px-4 py-3 text-zinc-500">{l.industry || "—"}</td>
                  <td className="whitespace-nowrap px-4 py-3 text-zinc-500">{l.interest || "—"}</td>
                  <td className="whitespace-nowrap px-4 py-3 font-mono text-xs text-cyan-600">{l.scheduled_slot || "—"}</td>
                  <td className="max-w-xs truncate px-4 py-3 text-zinc-500">{l.message || "—"}</td>
                  <td className="whitespace-nowrap px-4 py-3 font-mono text-xs text-zinc-400">{new Date(l.created_at).toLocaleDateString()}</td>
                </tr>
                );
              })}
            </tbody>
          </table>
        ) : tab === "newsletter" ? (
          <table data-testid="admin-newsletter-table" className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-zinc-900/10 bg-[#FFFFFF]">
                <th className="px-4 py-3 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">Email</th>
                <th className="px-4 py-3 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">Subscribed</th>
              </tr>
            </thead>
            <tbody>
              {newsletter.length === 0 && <tr><td colSpan={2} className="px-4 py-10 text-center text-zinc-400">No subscribers yet.</td></tr>}
              {newsletter.map((n) => (
                <tr key={n._id || n.id} className="border-b border-zinc-900/5 transition-colors hover:bg-zinc-900/[0.03]">
                  <td className="px-4 py-3 text-cyan-600">{n.email}</td>
                  <td className="px-4 py-3 font-mono text-xs text-zinc-400">{new Date(n.created_at).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : tab === "roi" ? (
          <table data-testid="admin-roi-table" className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-zinc-900/10 bg-[#FFFFFF]">
                {["Email", "Team Size", "Hourly Rate", "Est. Annual Savings", "Date"].map((h) => (
                  <th key={h} className="whitespace-nowrap px-4 py-3 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {roiLeads.length === 0 && <tr><td colSpan={5} className="px-4 py-10 text-center text-zinc-400">No ROI calculator leads yet.</td></tr>}
              {roiLeads.map((r) => (
                <tr key={r._id || r.id} className="border-b border-zinc-900/5 transition-colors hover:bg-zinc-900/[0.03]">
                  <td className="px-4 py-3 text-cyan-600">{r.email}</td>
                  <td className="px-4 py-3 text-zinc-700">{r.team_size}</td>
                  <td className="px-4 py-3 text-zinc-500">${r.hourly_rate}/hr</td>
                  <td className="px-4 py-3 font-mono text-cyan-600">${r.estimated_annual_savings.toLocaleString()}</td>
                  <td className="px-4 py-3 font-mono text-xs text-zinc-400">{new Date(r.created_at).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : tab === "ms" ? (
          <table data-testid="admin-ms-table" className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-zinc-900/10 bg-[#FFFFFF]">
                {["Status", "Name", "Company", "Industry", "Contact", "Top Opportunity", "Bottleneck", "Date"].map((h) => (
                  <th key={h} className="whitespace-nowrap px-4 py-3 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {msLeads.length === 0 && <tr><td colSpan={8} className="px-4 py-10 text-center text-zinc-400">No managed services leads yet.</td></tr>}
              {msLeads.map((m) => {
                const id = m._id || m.id;
                const top = m.results?.["Automate Immediately"]?.[0] || m.results?.["AI-Assisted"]?.[0] || "—";
                return (
                  <tr key={id} className="border-b border-zinc-900/5 transition-colors hover:bg-zinc-900/[0.03]">
                    <td className="whitespace-nowrap px-4 py-2">
                      <Select value={m.status || "new"} onValueChange={(v) => updateMsStatus(id, v)}>
                        <SelectTrigger data-testid={`ms-status-select-${id}`} className="h-8 w-[130px] rounded-none border border-cyan-500/50 bg-white font-mono text-[10px] uppercase tracking-[0.15em] text-cyan-700">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="rounded-none border-zinc-900/15 bg-white text-zinc-900">
                          {["new", "contacted", "qualified", "assessment", "proposal", "won", "lost", "nurture"].map((s) => <SelectItem key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-zinc-900">{m.name}</td>
                    <td className="whitespace-nowrap px-4 py-3 text-zinc-700">{m.company}</td>
                    <td className="whitespace-nowrap px-4 py-3 text-zinc-500">{m.industry || "—"}</td>
                    <td className="whitespace-nowrap px-4 py-3 text-cyan-600">{m.email}{m.phone ? ` · ${m.phone}` : ""}</td>
                    <td className="max-w-xs truncate px-4 py-3 text-zinc-500">{top}</td>
                    <td className="max-w-xs truncate px-4 py-3 text-zinc-500">{m.bottleneck || "—"}</td>
                    <td className="whitespace-nowrap px-4 py-3 font-mono text-xs text-zinc-400">{new Date(m.created_at).toLocaleDateString()}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : (
          <div data-testid="admin-engagements">
            <form onSubmit={addEngagement} className="flex flex-wrap gap-3 border-b border-zinc-900/10 bg-[#F0F2F5] p-4">
              {[["client", "Client *"], ["service", "Service *"], ["specialist", "Specialist"], ["monthly_revenue", "Monthly revenue $"], ["monthly_cost", "Monthly cost $"]].map(([k, ph]) => (
                <input key={k} data-testid={`eng-${k}-input`} required={ph.includes("*")} placeholder={ph}
                  type={k.startsWith("monthly") ? "number" : "text"} value={engForm[k]}
                  onChange={(e) => setEngForm({ ...engForm, [k]: e.target.value })}
                  className="w-40 border border-zinc-900/20 bg-white px-3 py-2 text-sm text-zinc-900 placeholder:text-zinc-400 focus:border-cyan-500 focus:outline-none" />
              ))}
              <button data-testid="eng-add-btn" type="submit" className="btn-primary !px-5 !py-2 text-xs">Add</button>
            </form>
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-zinc-900/10 bg-[#FFFFFF]">
                  {["Client", "Service", "Specialist", "Revenue/mo", "Cost/mo", "Gross Margin", "Status", ""].map((h, i) => (
                    <th key={i} className="whitespace-nowrap px-4 py-3 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {engagements.length === 0 && <tr><td colSpan={8} className="px-4 py-10 text-center text-zinc-400">No engagements tracked yet.</td></tr>}
                {engagements.map((g) => {
                  const id = g._id || g.id;
                  const margin = (g.monthly_revenue || 0) - (g.monthly_cost || 0);
                  const pct = g.monthly_revenue ? Math.round((margin / g.monthly_revenue) * 100) : 0;
                  return (
                    <tr key={id} className="border-b border-zinc-900/5 transition-colors hover:bg-zinc-900/[0.03]">
                      <td className="whitespace-nowrap px-4 py-3 text-zinc-900">{g.client}</td>
                      <td className="whitespace-nowrap px-4 py-3 text-zinc-700">{g.service}</td>
                      <td className="whitespace-nowrap px-4 py-3 text-zinc-500">{g.specialist || "—"}</td>
                      <td className="whitespace-nowrap px-4 py-3 font-mono text-zinc-700">${(g.monthly_revenue || 0).toLocaleString()}</td>
                      <td className="whitespace-nowrap px-4 py-3 font-mono text-zinc-500">${(g.monthly_cost || 0).toLocaleString()}</td>
                      <td className={`whitespace-nowrap px-4 py-3 font-mono ${margin >= 0 ? "text-cyan-600" : "text-red-600"}`}>${margin.toLocaleString()} ({pct}%)</td>
                      <td className="whitespace-nowrap px-4 py-2">
                        <Select value={g.status || "active"} onValueChange={(v) => updateEngStatus(g, v)}>
                          <SelectTrigger data-testid={`eng-status-${id}`} className="h-8 w-[110px] rounded-none border border-zinc-900/20 bg-white font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-700">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="rounded-none border-zinc-900/15 bg-white text-zinc-900">
                            {["active", "paused", "ended"].map((s) => <SelectItem key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="px-4 py-2">
                        <button data-testid={`eng-delete-${id}`} onClick={() => deleteEngagement(id)} className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-400 transition-colors hover:text-red-600">Remove</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </main>
  );
}
